import React, { useEffect, useState } from "react";
import { CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import { FiImage } from "react-icons/fi";
import { UiModal } from "../../../../Components/Shared/UiModal";

const SUBTYPE_REQUIRED = ["architecture", "interior", "contracts"];

function HomeScreenEntryModal({
  isOpen,
  onClose,
  editingItem,
  types,
  getHomeScreenSubTypes,
  onSubmit,
}) {
  const [linkValue, setLinkValue] = useState("");
  const [selectedTypeId, setSelectedTypeId] = useState("");
  const [selectedSubTypeId, setSelectedSubTypeId] = useState("");
  const [subTypes, setSubTypes] = useState([]);
  const [imagePreview, setImagePreview] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [imageError, setImageError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [attempted, setAttempted] = useState(false);

  const isEditMode = Boolean(editingItem);

  useEffect(() => {
    if (!isOpen) {
      setLinkValue("");
      setSelectedImage(null);
      setImagePreview(null);
      setImageError("");
      setSelectedTypeId("");
      setSelectedSubTypeId("");
      setSubTypes([]);
      setIsSubmitting(false);
      setAttempted(false);
      return;
    }

    setLinkValue(editingItem?.link || "");
    setSelectedImage(null);
    setImagePreview(editingItem?.image || null);
    setImageError("");
    setSelectedTypeId(editingItem?.type?.id?.toString() || "");
    setSelectedSubTypeId(editingItem?.subType?.id?.toString() || "");
    setIsSubmitting(false);
    setAttempted(false);
  }, [isOpen, editingItem]);

  useEffect(() => {
    if (!isOpen) return undefined;

    if (!selectedTypeId) {
      setSubTypes([]);
      return undefined;
    }

    let cancelled = false;
    const fetchSub = async () => {
      const st = await getHomeScreenSubTypes(selectedTypeId);
      if (!cancelled) setSubTypes(st);
    };
    fetchSub();
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedTypeId]);

  const selectedTypeObj = types.find(
    (t) => t.id.toString() === selectedTypeId,
  );
  const requiresSubType =
    selectedTypeObj &&
    SUBTYPE_REQUIRED.includes(selectedTypeObj.name.toLowerCase());

  const validateImageDimensions = (file) =>
    new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        if (img.width === 800 && img.height === 500) {
          resolve(true);
        } else {
          reject(
            new Error(
              `Image dimensions must be exactly 800x500 pixels. Current dimensions: ${img.width}x${img.height}`,
            ),
          );
        }
      };
      img.onerror = () => reject(new Error("Failed to load image"));
      img.src = URL.createObjectURL(file);
    });

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    e.target.value = "";
    if (!file) return;

    setImageError("");

    if (!file.type.startsWith("image/")) {
      setImageError("Please select a valid image file.");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setImageError("Image size must be less than 5MB.");
      return;
    }

    try {
      await validateImageDimensions(file);
      setSelectedImage(file);

      const reader = new FileReader();
      reader.onload = (event) => {
        setImagePreview(event.target.result);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      setImageError(error.message);
      setSelectedImage(null);
      setImagePreview(editingItem?.image || null);
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    setImagePreview(editingItem?.image || null);
    setImageError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;

    setAttempted(true);

    if (!linkValue?.trim()) {
      return;
    }

    if (!selectedTypeId) {
      return;
    }

    if (requiresSubType && !selectedSubTypeId) {
      return;
    }

    if (!isEditMode && !selectedImage) {
      setImageError("Image is required.");
      return;
    }

    const payload = {
      link: linkValue.trim(),
      typeId: selectedTypeId,
      subTypeId: selectedSubTypeId,
    };

    if (selectedImage) {
      payload.image = selectedImage;
    }

    setIsSubmitting(true);
    try {
      await onSubmit(payload);
    } finally {
      setIsSubmitting(false);
    }
  };

  const missingLink = attempted && !linkValue?.trim();
  const missingType = attempted && !selectedTypeId;
  const missingSubType = attempted && requiresSubType && !selectedSubTypeId;
  const missingImage = !isEditMode && !selectedImage;

  return (
    <UiModal
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Home Screen"
      title={isEditMode ? "Edit Entry" : "Add Entry"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            className="ui-btn-cancel min-w-[108px]"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="ui-btn-save min-w-[108px]"
            disabled={isSubmitting || missingImage}
          >
            {isSubmitting ? "Saving..." : isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Media</h4>
        <div className="space-y-3">
          <label className="ui-form-label">
            Image {!isEditMode && <span className="text-red-500">*</span>}
          </label>

          <input
            id="home-screen-image-upload"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />

          {imagePreview ? (
            <div className="group relative w-full overflow-hidden rounded-xl border border-gray-200 bg-stone-50 shadow-sm">
              <div className="aspect-[8/5] w-full">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="h-full w-full object-contain"
                />
              </div>

              <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-2 bg-gradient-to-t from-black/70 via-black/35 to-transparent px-3 pb-3 pt-10">
                <div className="min-w-0">
                  <p className="truncate text-xs font-medium text-white">
                    {selectedImage
                      ? selectedImage.name
                      : isEditMode
                        ? "Current image"
                        : "Selected image"}
                  </p>
                  <p className="text-[11px] text-white/75">800 × 500 · max 5MB</p>
                </div>

                <div className="flex shrink-0 items-center gap-2">
                  <label
                    htmlFor="home-screen-image-upload"
                    className="btn btn-edit h-9 cursor-pointer px-3 text-sm text-white"
                    title={isEditMode ? "Change image" : "Replace image"}
                  >
                    <FiImage className="text-white" />
                    {isEditMode ? "Change Image" : "Replace Image"}
                  </label>

                  {selectedImage && (
                    <button
                      type="button"
                      onClick={handleRemoveImage}
                      className="btn btn-delete h-9 px-3 text-sm text-white"
                      title="Remove selected image"
                    >
                      <TrashIcon className="h-4 w-4 text-white" />
                      Remove
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <label
              htmlFor="home-screen-image-upload"
              className="flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-[#982598]/50 hover:bg-[#982598]/5"
            >
              <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                <CameraIcon className="h-6 w-6 text-[#982598]" />
              </span>
              <span className="font-medium text-gray-700">Upload Image</span>
              <span className="mt-1 text-sm text-gray-400">
                Click to browse files
              </span>
              <span className="mt-2 rounded-full bg-white px-3 py-1 text-xs text-gray-500 shadow-sm">
                Required: 800 × 500 px
              </span>
            </label>
          )}

          {imageError ? (
            <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600">
              {imageError}
            </p>
          ) : null}
          <p className="text-xs text-gray-500">
            Image must be exactly 800×500 pixels and less than 5MB
            {isEditMode ? ". Leave unchanged to keep the current image." : ""}
          </p>
        </div>
      </section>

      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Entry details</h4>
        <div className="space-y-4">
          <div>
            <label className="ui-form-label">
              Type <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-2">
              {types.map((type) => (
                <label
                  key={type.id}
                  className="flex cursor-pointer items-center space-x-2"
                >
                  <input
                    type="radio"
                    name="type"
                    value={type.id}
                    checked={selectedTypeId === type.id.toString()}
                    onChange={(e) => {
                      setSelectedTypeId(e.target.value);
                      setSelectedSubTypeId("");
                    }}
                    className="form-radio cursor-pointer text-blue-600"
                  />
                  <span>{type.name}</span>
                </label>
              ))}
            </div>
            {missingType ? (
              <p className="ui-form-error">Type is required.</p>
            ) : null}
          </div>

          {requiresSubType && subTypes.length > 0 ? (
            <div>
              <label className="ui-form-label">
                Sub Type <span className="text-red-500">*</span>
              </label>
              <div className="grid grid-cols-2 gap-2">
                {subTypes.map((sub) => (
                  <label
                    key={sub.id}
                    className="flex cursor-pointer items-center space-x-2"
                  >
                    <input
                      type="radio"
                      name="subType"
                      value={sub.id}
                      checked={selectedSubTypeId === sub.id.toString()}
                      onChange={(e) => setSelectedSubTypeId(e.target.value)}
                      className="form-radio cursor-pointer text-blue-600"
                    />
                    <span>{sub.name}</span>
                  </label>
                ))}
              </div>
              {missingSubType ? (
                <p className="ui-form-error">Sub Type is required.</p>
              ) : null}
            </div>
          ) : null}

          <div>
            <label className="ui-form-label">
              Link <span className="text-red-500">*</span>
            </label>
            <input
              type="url"
              name="link"
              value={linkValue}
              onChange={(e) => setLinkValue(e.target.value)}
              className={`ui-input-field ${
                missingLink ? "border-brand-error" : ""
              }`}
              placeholder="Enter link..."
              required
            />
            {missingLink ? (
              <p className="ui-form-error">Link is required.</p>
            ) : null}
          </div>
        </div>
      </section>
    </UiModal>
  );
}

export default HomeScreenEntryModal;
